import { forwardRef, useCallback, useId, useRef, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';
import { locatePath } from '@/shared/utils/parse-json/parse-json.util';
import { countLines } from '@/shared/utils/text-lines/text-lines.util';
import { cn } from '@/shared/utils/cn/cn.util';

import { JsonGutter } from '../JsonGutter/JsonGutter';
import { JsonToolbar } from '../JsonToolbar/JsonToolbar';
import { StructuredIssueList } from '../StructuredIssueList/StructuredIssueList';
import { StructuredShell } from '../StructuredShell/StructuredShell';
import {
  JSON_HINT_CLASSES,
  JSON_ISLAND_CLASSES,
  JSON_ISLAND_INVALID_CLASSES,
  JSON_MESSAGE_CLASSES,
  JSON_TEXTAREA_CLASSES,
  KEY_VALUE_ROW_ERROR_CLASSES,
  KEY_VALUE_ROW_WARNING_CLASSES,
} from '../structured.tokens';
import { type StructuredIssuePath } from '../types/structured-issue.types';

import { type JsonEditorProps } from './JsonEditor.types';
import { useJsonEditorLogic } from './useJsonEditorLogic';

/**
 * The raw editor behind a shape no control can express (`ai.pricing`,
 * `trust.signals`, `brand.legalEntity`).
 *
 * What changed in P10.2b: it says **where** the JSON broke rather than only
 * that it did, it numbers its lines and marks the one that failed, it grows with
 * its content instead of sitting at a fixed ten rows, and it can be formatted
 * and copied. The editor island is `dir="ltr"` while everything around it stays
 * in the interface direction — JSON is code, and code has one direction.
 *
 * All the behaviour is in `useJsonEditorLogic` (U5).
 */
export const JsonEditor = forwardRef<HTMLTextAreaElement, JsonEditorProps>(function JsonEditor(
  {
    id,
    className,
    size,
    label,
    'aria-describedby': describedBy,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    issues,
    value,
    onValueChange,
    minRows,
    maxRows,
    hasLineNumbers = true,
    onParseFailureChange,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const generatedId = useId();
  const messageId = useId();
  const issuesId = useId();
  const fieldId = id ?? generatedId;

  const textarea = useRef<HTMLTextAreaElement | null>(null);
  const gutter = useRef<HTMLDivElement | null>(null);

  const logic = useJsonEditorLogic({
    value,
    onValueChange,
    minRows,
    maxRows,
    isDisabled,
    isReadOnly,
    ...(onParseFailureChange === undefined ? {} : { onParseFailureChange }),
  });

  const hasIssues = (issues ?? []).length > 0;
  const isBroken = logic.failure !== null;

  const goTo = useCallback((offset: number) => {
    const element = textarea.current;
    if (element === null) return;

    element.focus();
    element.setSelectionRange(offset, offset);
  }, []);

  /**
   * The gutter is scrolled BY the textarea, in the scroll handler.
   *
   * An imperative write in an event handler rather than an effect: there is
   * nothing to derive and nothing to render — the two boxes simply have to agree
   * about which line is at the top.
   */
  const syncGutter = useCallback((event: UIEvent<HTMLTextAreaElement>) => {
    if (gutter.current !== null) gutter.current.scrollTop = event.currentTarget.scrollTop;
  }, []);

  const describedByIds =
    [describedBy, isBroken ? messageId : null, hasIssues ? issuesId : null]
      .filter((candidate) => candidate !== null && candidate !== undefined)
      .join(' ') || undefined;

  return (
    <StructuredShell
      className={className}
      label={label}
      isReadOnly={isReadOnly}
      announcement={logic.announcement}
      toolbar={
        isDisabled ? undefined : (
          <JsonToolbar
            isExpanded={logic.isExpanded}
            onCopy={() => {
              void logic.copy();
            }}
            {...(isReadOnly ? {} : { onFormat: logic.format })}
            {...(logic.isOverflowing || logic.isExpanded
              ? { onToggleExpanded: logic.toggleExpanded }
              : {})}
          />
        )
      }
      footer={
        <>
          {isBroken ? (
            <p id={messageId} role="alert" className={JSON_MESSAGE_CLASSES}>
              <span className={KEY_VALUE_ROW_ERROR_CLASSES}>
                {logic.failure?.line === null
                  ? t('json.invalid')
                  : t('json.invalidAt', {
                      reason: t(`json.reason.${logic.failure?.code ?? 'unknown'}`),
                      line: logic.failure?.line,
                      column: logic.failure?.column,
                    })}
              </span>
              {logic.errorOffset === null ? null : (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => {
                    goTo(logic.errorOffset ?? 0);
                  }}
                >
                  {t('json.goToError')}
                </Button>
              )}
            </p>
          ) : (
            <p className={JSON_HINT_CLASSES}>{t('json.hint')}</p>
          )}

          {logic.duplicateKeys.length === 0 ? null : (
            <p className={KEY_VALUE_ROW_WARNING_CLASSES}>
              {t('json.duplicateKeys', { keys: logic.duplicateKeys.join('، ') })}
            </p>
          )}

          <StructuredIssueList
            id={issuesId}
            title={t('structured.issuesTitle')}
            issues={issues ?? []}
            offsetOf={(path: StructuredIssuePath) => locatePath(logic.text, path)}
            onGoTo={goTo}
          />
        </>
      }
    >
      {/*
        The island carries `dir="ltr"` and NOTHING around it does: the label, the
        toolbar, the error and the issue list all stay in the interface
        direction. That is why the gutter lands on the physical left in Arabic
        as well as English without a single direction-branching class.
      */}
      <div
        dir="ltr"
        className={cn(JSON_ISLAND_CLASSES, (isInvalid || isBroken) && JSON_ISLAND_INVALID_CLASSES)}
      >
        {hasLineNumbers ? (
          <JsonGutter
            lineCount={countLines(logic.text)}
            errorLine={logic.errorLine}
            scrollRef={gutter}
          />
        ) : null}

        <Textarea
          ref={(element) => {
            textarea.current = element;
            if (typeof ref === 'function') ref(element);
            else if (ref !== null) ref.current = element;
          }}
          id={fieldId}
          aria-label={label}
          aria-describedby={describedByIds}
          aria-invalid={isInvalid || isBroken ? true : undefined}
          // No `aria-readonly` here: the NATIVE `readOnly` below already carries
          // that state on a textbox, and duplicating a native semantic in ARIA
          // is how a state gets announced twice. The switch and the select in
          // the key/value editor need the attribute because neither has a native
          // read-only to inherit.
          value={logic.text}
          rows={logic.rows}
          disabled={isDisabled}
          readOnly={isReadOnly}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          textareaSize={size}
          // NO soft wrap. That is what makes one logical line exactly one line
          // box — and therefore what makes `rows` exact, the gutter aligned, and
          // the whole thing measurement-free.
          wrap="off"
          className={cn(JSON_TEXTAREA_CLASSES, 'resize-none overflow-auto scrollbar-thin')}
          onScroll={syncGutter}
          onChange={(event) => {
            logic.setText(event.target.value);
          }}
          onBlur={() => {
            logic.commit();
            onBlur?.();
          }}
        />
      </div>
    </StructuredShell>
  );
});
